import { type Config } from 'tailwindcss'

export const extendedTheme = {
	colors: {
		border: 'hsl(var(--border))',
		input: {
			DEFAULT: 'hsl(var(--input))',
			invalid: 'hsl(var(--input-invalid))',
		},
		ring: {
			DEFAULT: 'hsl(var(--ring))',
			invalid: 'hsl(var(--foreground-destructive))',
		},
		background: 'hsl(var(--background))',
		foreground: {
			DEFAULT: 'hsl(var(--foreground))',
			destructive: 'hsl(var(--foreground-destructive))',
		},
		primary: {
			DEFAULT: 'hsl(var(--primary))',
			foreground: 'hsl(var(--primary-foreground))',
		},
		secondary: {
			DEFAULT: 'hsl(var(--secondary))',
			foreground: 'hsl(var(--secondary-foreground))',
		},
		destructive: {
			DEFAULT: 'hsl(var(--destructive))',
			foreground: 'hsl(var(--destructive-foreground))',
		},
		muted: {
			DEFAULT: 'hsl(var(--muted))',
			foreground: 'hsl(var(--muted-foreground))',
		},
		accent: {
			DEFAULT: 'hsl(var(--accent))',
			foreground: 'hsl(var(--accent-foreground))',
		},
		popover: {
			DEFAULT: 'hsl(var(--popover))',
			foreground: 'hsl(var(--popover-foreground))',
		},
		card: {
			DEFAULT: 'hsl(var(--card))',
			foreground: 'hsl(var(--card-foreground))',
		},
		sidebar: {
			DEFAULT: 'hsl(var(--sidebar-background))',
			foreground: 'hsl(var(--sidebar-foreground))',
			primary: 'hsl(var(--sidebar-primary))',
			'primary-foreground': 'hsl(var(--sidebar-primary-foreground))',
			accent: 'hsl(var(--sidebar-accent))',
			'accent-foreground': 'hsl(var(--sidebar-accent-foreground))',
			border: 'hsl(var(--sidebar-border))',
			ring: 'hsl(var(--sidebar-ring))',
		},
	},
	borderColor: {
		DEFAULT: 'hsl(var(--border))',
	},
	borderRadius: {
		lg: 'var(--radius)',
		md: 'calc(var(--radius) - 2px)',
		sm: 'calc(var(--radius) - 4px)',
	},
	fontSize: {
		// 1rem = 16px
		/** 80px size / 84px high / bold */
		mega: ['5rem', { lineHeight: '5.25rem', fontWeight: '700' }],
		/** 56px size / 62px high / bold */
		h1: ['3.5rem', { lineHeight: '3.875rem', fontWeight: '700' }],
		/** 40px size / 48px high / bold */
		h2: ['2.5rem', { lineHeight: '3rem', fontWeight: '700' }],
		/** 32px size / 36px high / bold */
		h3: ['2rem', { lineHeight: '2.25rem', fontWeight: '700' }],
		/** 28px size / 36px high / bold */
		h4: ['1.75rem', { lineHeight: '2.25rem', fontWeight: '700' }],
		/** 24px size / 32px high / bold */
		h5: ['1.5rem', { lineHeight: '2rem', fontWeight: '700' }],
		/** 16px size / 20px high / bold */
		h6: ['1rem', { lineHeight: '1.25rem', fontWeight: '700' }],

		/** 32px size / 36px high / normal */
		'body-2xl': ['2rem', { lineHeight: '2.25rem' }],
		/** 28px size / 36px high / normal */
		'body-xl': ['1.75rem', { lineHeight: '2.25rem' }],
		/** 24px size / 32px high / normal */
		'body-lg': ['1.5rem', { lineHeight: '2rem' }],
		/** 20px size / 28px high / normal */
		'body-md': ['1.25rem', { lineHeight: '1.75rem' }],
		/** 16px size / 20px high / normal */
		'body-sm': ['1rem', { lineHeight: '1.25rem' }],
		/** 14px size / 18px high / normal */
		'body-xs': ['0.875rem', { lineHeight: '1.125rem' }],
		/** 12px size / 16px high / normal */
		'body-2xs': ['0.75rem', { lineHeight: '1rem' }],

		/** 18px size / 24px high / semibold */
		caption: ['1.125rem', { lineHeight: '1.5rem', fontWeight: '600' }],
		/** 12px size / 16px high / bold */
		button: ['0.75rem', { lineHeight: '1rem', fontWeight: '700' }],
	},

	animation: {
		// Modal
		'scale-in': 'scale-in 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
		'fade-in': 'fade-in 0.2s ease-out forwards',
		'scale-in-fade': 'scale-in-fade 0.2s ease-out forwards',
		// Popover, Tooltip
		'slide-up-fade': 'slide-up-fade 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
		'slide-right-fade': 'slide-right-fade 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
		'slide-down-fade': 'slide-down-fade 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
		'slide-left-fade': 'slide-left-fade 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
		// Sheet
		'slide-in-from-right': 'slide-in-from-right 0.2s ease',
		'slide-out-to-right': 'slide-out-to-right 0.2s ease',
		// Navigation menu
		'enter-from-right': 'enter-from-right 0.15s ease',
		'enter-from-left': 'enter-from-left 0.15s ease',
		'exit-to-right': 'exit-to-right 0.15s ease',
		'exit-to-left': 'exit-to-left 0.15s ease',
		'scale-in-content': 'scale-in-content 0.2s ease',
		'scale-out-content': 'scale-out-content 0.2s ease',
		// Accordion
		'accordion-down': 'accordion-down 300ms cubic-bezier(0.87, 0, 0.13, 1)',
		'accordion-up': 'accordion-up 300ms cubic-bezier(0.87, 0, 0.13, 1)',
		// Custom wiggle animation
		wiggle: 'wiggle 0.75s infinite',
		// Custom spinner animation (for loading-spinner)
		spinner: 'spinner 1.2s linear infinite',
		// Custom blink animation (for loading-dots)
		blink: 'blink 1.4s infinite both',
		// Custom pulse animation
		pulse: 'pulse 1s linear infinite alternate',
		'caret-blink': 'caret-blink 1.25s ease-out infinite',
	},
	keyframes: {
		// Modal
		'scale-in': {
			'0%': { transform: 'scale(0.95)' },
			'100%': { transform: 'scale(1)' },
		},
		'fade-in': {
			'0%': { opacity: '0' },
			'100%': { opacity: '1' },
		},
		'scale-in-fade': {
			'0%': { transform: 'scale(0.95)', opacity: '0' },
			'100%': { transform: 'scale(1)', opacity: '1' },
		},
		// Popover, Tooltip
		'slide-up-fade': {
			'0%': { opacity: '0', transform: 'translateY(var(--offset, 2px))' },
			'100%': { opacity: '1', transform: 'translateY(0)' },
		},
		'slide-right-fade': {
			'0%': { opacity: '0', transform: 'translateX(var(--offset, -2px))' },
			'100%': { opacity: '1', transform: 'translateX(0)' },
		},
		'slide-down-fade': {
			'0%': { opacity: '0', transform: 'translateY(var(--offset, -2px))' },
			'100%': { opacity: '1', transform: 'translateY(0)' },
		},
		'slide-left-fade': {
			'0%': { opacity: '0', transform: 'translateX(var(--offset, 2px))' },
			'100%': { opacity: '1', transform: 'translateX(0)' },
		},
		// Sheet
		'slide-in-from-right': {
			'0%': { transform: 'translateX(100%)' },
			'100%': { transform: 'translateX(0)' },
		},
		'slide-out-to-right': {
			'0%': { transform: 'translateX(0)' },
			'100%': { transform: 'translateX(100%)' },
		},
		// Navigation menu
		'enter-from-right': {
			'0%': { transform: 'translateX(200px)', opacity: '0' },
			'100%': { transform: 'translateX(0)', opacity: '1' },
		},
		'enter-from-left': {
			'0%': { transform: 'translateX(-200px)', opacity: '0' },
			'100%': { transform: 'translateX(0)', opacity: '1' },
		},
		'exit-to-right': {
			'0%': { transform: 'translateX(0)', opacity: '1' },
			'100%': { transform: 'translateX(200px)', opacity: '0' },
		},
		'exit-to-left': {
			'0%': { transform: 'translateX(0)', opacity: '1' },
			'100%': { transform: 'translateX(-200px)', opacity: '0' },
		},
		'scale-in-content': {
			'0%': { transform: 'rotateX(-30deg) scale(0.9)', opacity: '0' },
			'100%': { transform: 'rotateX(0deg) scale(1)', opacity: '1' },
		},
		'scale-out-content': {
			'0%': { transform: 'rotateX(0deg) scale(1)', opacity: '1' },
			'100%': { transform: 'rotateX(-10deg) scale(0.95)', opacity: '0' },
		},
		// Accordion
		'accordion-down': {
			from: { height: '0' },
			to: { height: 'var(--radix-accordion-content-height)' },
		},
		'accordion-up': {
			from: { height: 'var(--radix-accordion-content-height)' },
			to: { height: '0' },
		},
		// Custom wiggle animation
		wiggle: {
			'0%, 100%': {
				transform: 'translateX(0%)',
				transformOrigin: '50% 50%',
			},
			'15%': { transform: 'translateX(-4px) rotate(-4deg)' },
			'30%': { transform: 'translateX(6px) rotate(4deg)' },
			'45%': { transform: 'translateX(-6px) rotate(-2.4deg)' },
			'60%': { transform: 'translateX(2px) rotate(1.6deg)' },
			'75%': { transform: 'translateX(-1px) rotate(-0.8deg)' },
		},
		// Custom spinner animation (for loading-spinner)
		spinner: {
			'0%': {
				opacity: '1',
			},
			'100%': {
				opacity: '0',
			},
		},
		// Custom blink animation (for loading-dots)
		blink: {
			'0%': {
				opacity: '0.2',
			},
			'20%': {
				opacity: '1',
			},
			'100%': {
				opacity: '0.2',
			},
		},
		// Custom pulse animation
		pulse: {
			from: {
				opacity: '0',
			},
			to: {
				opacity: '1',
			},
		},
		'caret-blink': {
			'0%,70%,100%': { opacity: '1' },
			'20%,50%': { opacity: '0' },
		},
	},
} satisfies Config['theme']
